/**
 * Тост-уведомление канбана (#34): всплывает в правом верхнем углу экрана,
 * с крестиком и авто-скрытием. Переиспользуемо в пределах канбана (и шире —
 * можно вынести в @hsc/ui позже). Раньше ошибка показывалась нижним баннером
 * и при длинном списке заявок не была видна — теперь видна всегда сверху.
 *
 * i18n: компонент только рендерит переданный текст (RU формируется на месте
 * вызова).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type ToastTone = 'error' | 'success' | 'info';

export interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
}

/** Стек тостов + API показать/закрыть. Авто-скрытие управляется в Toaster. */
export function useToasts() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((tone: ToastTone, message: string) => {
    seq.current += 1;
    const id = seq.current;
    setToasts((cur) => [...cur, { id, tone, message }]);
    return id;
  }, []);

  const error = useCallback((message: string) => push('error', message), [push]);
  const success = useCallback((message: string) => push('success', message), [push]);

  return { toasts, push, error, success, dismiss };
}

interface ToastViewProps {
  toast: ToastItem;
  onDismiss: (id: number) => void;
  /** Авто-скрытие через N мс (0 — не скрывать автоматически). */
  autoHideMs: number;
}

function ToastView({ toast, onDismiss, autoHideMs }: ToastViewProps): JSX.Element {
  useEffect(() => {
    if (autoHideMs <= 0) return undefined;
    const t = setTimeout(() => onDismiss(toast.id), autoHideMs);
    return () => clearTimeout(t);
  }, [toast.id, autoHideMs, onDismiss]);

  return (
    <div
      className={`rqk-toast rqk-toast--${toast.tone}`}
      role={toast.tone === 'error' ? 'alert' : 'status'}
      data-testid={`kanban-toast-${toast.tone}`}
    >
      <span className="rqk-toast__msg">{toast.message}</span>
      <button
        type="button"
        className="rqk-toast__close"
        aria-label="Закрыть уведомление"
        data-testid="kanban-toast-close"
        onClick={() => onDismiss(toast.id)}
      >
        ×
      </button>
    </div>
  );
}

export interface ToasterProps {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
  autoHideMs?: number;
}

const DEFAULT_AUTO_HIDE_MS = 5000;

/** Контейнер тостов — фиксирован в правом верхнем углу вьюпорта. */
export function Toaster({ toasts, onDismiss, autoHideMs }: ToasterProps): JSX.Element | null {
  if (toasts.length === 0) return null;
  const hideMs = autoHideMs ?? DEFAULT_AUTO_HIDE_MS;
  return (
    <div className="rqk-toaster" data-testid="kanban-toaster" aria-live="polite">
      {toasts.map((t) => (
        <ToastView key={t.id} toast={t} onDismiss={onDismiss} autoHideMs={hideMs} />
      ))}
    </div>
  );
}
